import { ArrowDownToLine, Copy, WrapText } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../lib/cn.ts';
import { plural } from '../lib/format.ts';
import { toast } from '../lib/toast.ts';
import { Button } from './Button.tsx';
import { ErrorState } from './ErrorState.tsx';
import { IconButton } from './IconButton.tsx';
import { SkeletonText } from './Skeleton.tsx';
import { StatusDot } from './StatusDot.tsx';

export interface LogViewerProps {
  /** The log so far. ANSI colour codes are stripped. */
  text: string;
  /** Still streaming: follows new lines while scrolled to the bottom. */
  live?: boolean;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void | Promise<void>;
  /** Shown when the stream is open but has said nothing yet. */
  emptyText?: string;
  /** Accessible name, for example "Log for feat(approvals)". */
  label: string;
  className?: string;
}

// Built from a string so no control character sits in the source.
const ANSI = new RegExp(`${String.fromCharCode(27)}\\[[0-9;?]*[ -/]*[@-~]`, 'g');
const MAX_LINES = 5000;

function lineTone(line: string): string | undefined {
  if (/\b(error|failed|fail|fatal)\b|✗|✖/i.test(line)) return 'text-failed';
  if (/\b(warn|warning)\b/i.test(line)) return 'text-waiting';
  if (/\b(passed|pass|ok|done|success)\b|✓|✔/i.test(line)) return 'text-passed';
  return undefined;
}

/** A monospace live log: follows the tail, offers "Jump to latest" once you scroll up. */
export function LogViewer({ text, live, loading, error, onRetry, emptyText = 'No output yet.', label, className }: LogViewerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [follow, setFollow] = useState(true);
  const [wrap, setWrap] = useState(true);
  const lines = useMemo(() => {
    const all = text.replace(ANSI, '').replace(/\r\n?/g, '\n').split('\n');
    if (all.length && all[all.length - 1] === '') all.pop();
    return all.length > MAX_LINES ? all.slice(all.length - MAX_LINES) : all;
  }, [text]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el && follow) el.scrollTop = el.scrollHeight;
  }, [lines, follow]);

  useEffect(() => {
    setFollow(true);
  }, [label]);

  const onScroll = () => {
    const el = ref.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    if (atBottom !== follow) setFollow(atBottom);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      toast.success('Log copied', `${lines.length} lines on the clipboard.`);
    } catch (e) {
      toast.error(e);
    }
  };

  return (
    <div className={cn('relative flex min-h-0 flex-col overflow-hidden rounded-md border border-border bg-bg', className)}>
      <div className="flex h-8 shrink-0 items-center gap-2 border-b border-border-subtle pl-3 pr-1 text-sm text-fg-subtle">
        {live ? (
          <span className="flex items-center gap-1.5 text-fg-muted">
            <StatusDot tone="working" size="xs" label="" />
            Live
          </span>
        ) : (
          <span>{loading ? 'Opening' : 'Ended'}</span>
        )}
        <span className="num">{plural(lines.length, 'line')}</span>
        <span className="ml-auto flex items-center gap-0.5">
          <IconButton label={wrap ? 'Do not wrap lines' : 'Wrap long lines'} icon={<WrapText />} size="xs" pressed={wrap} onClick={() => setWrap((w) => !w)} />
          <IconButton label="Copy log" icon={<Copy />} size="xs" disabled={!lines.length} onClick={copy} />
        </span>
      </div>
      {loading && !lines.length ? (
        <div className="p-4" role="status" aria-label="Opening the log">
          <SkeletonText lines={6} />
        </div>
      ) : error != null && !lines.length ? (
        <ErrorState error={error} onRetry={onRetry} />
      ) : (
        <div
          ref={ref}
          onScroll={onScroll}
          role="log"
          aria-label={label}
          aria-live={live ? 'polite' : 'off'}
          tabIndex={0}
          className="min-h-0 flex-1 overflow-auto py-2 font-mono text-sm leading-[1.6] outline-offset-[-2px]"
        >
          {lines.length ? (
            lines.map((l, i) => (
              <div key={i} className="flex gap-3 px-3 hover:bg-hover/60">
                <span className="w-9 shrink-0 select-none text-right text-fg-faint num" aria-hidden>
                  {i + 1}
                </span>
                <span className={cn('min-w-0 flex-1', wrap ? 'whitespace-pre-wrap break-words' : 'whitespace-pre', lineTone(l) ?? 'text-fg-muted')}>
                  {l || ' '}
                </span>
              </div>
            ))
          ) : (
            <p className="px-3 text-fg-subtle">{emptyText}</p>
          )}
          {error != null && lines.length > 0 && (
            <div className="px-3 pt-2">
              <ErrorState error={error} onRetry={onRetry} variant="inline" />
            </div>
          )}
        </div>
      )}
      {!follow && lines.length > 0 && (
        <Button
          size="sm"
          variant="secondary"
          icon={<ArrowDownToLine />}
          className="absolute bottom-3 right-4 shadow-md"
          onClick={() => {
            setFollow(true);
            const el = ref.current;
            if (el) el.scrollTop = el.scrollHeight;
          }}
        >
          Jump to latest
        </Button>
      )}
    </div>
  );
}
